/* ---------------------------------------------------------------------------
   pm-cart-merge.js — 2026-08-24

   Pilote les contrôles des lignes FUSIONNÉES du cart drawer.

   Le problème. Shopify scinde une même variante en plusieurs lignes quand une remise
   BXGY ne porte que sur une partie de la quantité : 4 rouleaux identiques deviennent
   « 2 payés », « 1 offert », « 1 payé ». cart.ajax.liquid regroupe ces lignes à
   l'affichage — mais une ligne affichée couvre alors plusieurs lignes réelles, et les
   boutons + / − / × du thème, qui pilotent UNE ligne par son index, ne conviennent plus.

   Exemple du bug évité : la ligne fusionnée affiche 3 (= 1 + 2). Le thème enverrait
   « ligne 1 → 4 » et le panier passerait à 6 unités au lieu de 4.

   Ce qui a été écarté. `/cart/update.js` indexé par identifiant de variante paraissait
   idéal — un seul appel, un total par variante. Mesuré : sur un panier de 4 unités
   réparties en 3 lignes, il en a produit 7. Il n'écrit pas un total, il écrase une ligne
   et laisse Shopify réallouer. Seule la forme TABLEAU, positionnelle par ligne, est
   fiable en présence de doublons — c'est celle utilisée ici.

   Portée. Le script n'agit que sur les lignes portant data-pm-lines, c'est-à-dire celles
   qui couvrent réellement plusieurs lignes de panier. Une ligne simple garde le
   comportement natif du thème, sans interception.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  var busy = false;

  function nums(el, attr) {
    var v = el.getAttribute(attr);
    return v ? v.split(',').map(function (n) { return parseInt(n, 10); }) : [];
  }

  /* Applique de nouvelles quantités à des lignes précises.
     On relit /cart.js juste avant d'écrire : le DOM peut dater d'un rendu antérieur,
     et un tableau positionnel appliqué sur un panier qui a changé écrirait sur les
     mauvaises lignes. Si le nombre de lignes ne correspond plus, on renonce et on
     laisse le thème se resynchroniser plutôt que de corrompre le panier. */
  function applyLines(lines, qtys, expectedCount) {
    if (busy) return;
    busy = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.items.length !== expectedCount) throw new Error('panier désynchronisé');

        var updates = cart.items.map(function (i) { return i.quantity; });
        for (var k = 0; k < lines.length; k++) {
          var idx = lines[k] - 1;                 // data-pm-lines est en base 1
          if (idx < 0 || idx >= updates.length) throw new Error('ligne hors panier');
          updates[idx] = Math.max(0, qtys[k]);
        }

        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })
      .then(function () {
        if (window.theme && theme.ajaxCart && theme.ajaxCart.update) {
          theme.ajaxCart.update();            // re-rend le drawer et re-attache les handlers
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        /* En cas de doute on ne devine pas : on redemande l'état au serveur. */
        if (window.theme && theme.ajaxCart && theme.ajaxCart.update) theme.ajaxCart.update();
      })
      .then(function () { busy = false; });
  }

  /* Retire `n` unités du groupe, en vidant les lignes de la plus petite à la plus grande.
     Vider d'abord les petites lignes fait disparaître les doublons résiduels au lieu de
     les entretenir : le panier tend vers une ligne unique au fil des retraits. */
  function decrement(qtys, n) {
    var out = qtys.slice();
    var order = out.map(function (q, i) { return i; })
                   .sort(function (a, b) { return out[a] - out[b]; });
    for (var pass = 0; pass < order.length && n > 0; pass++) {
      var i = order[pass];
      var take = Math.min(out[i], n);
      out[i] -= take;
      n -= take;
    }
    return out;
  }

  function onClick(e) {
    var row = e.target.closest && e.target.closest('[data-pm-lines]');
    if (!row) return;

    var plus = e.target.closest('.js-qty__adjust--plus');
    var minus = e.target.closest('.js-qty__adjust--minus');
    var remove = e.target.closest('.cart__product-remove');
    if (!plus && !minus && !remove) return;

    e.preventDefault();
    e.stopImmediatePropagation();               // neutralise le handler natif du thème
    if (busy) return;                           // une mise a jour est deja en cours

    var lines = nums(row, 'data-pm-lines');
    var qtys = nums(row, 'data-pm-qtys');
    var count = parseInt(row.getAttribute('data-pm-cartlines'), 10);

    var next;
    if (remove) {
      next = qtys.map(function () { return 0; });
    } else if (plus) {
      next = qtys.slice();
      next[0] = next[0] + 1;
    } else {
      next = decrement(qtys, 1);
    }

    row.classList.add('is-loading');
    // 2026-10-09 (Imran) : meme retour visuel que le theme — le bouton clique devient l'icone de
    // chargement. Sans ca, sur une ligne regroupee, le + restait fige ~1,5 s et paraissait bloque.
    // Le tiroir est re-rendu a la fin de applyLines(), ce qui remet les boutons a neuf.
    if (window.theme && theme.loadingState && theme.loadingState.init) theme.loadingState.init(plus || minus || remove);
    applyLines(lines, next, count);
  }

  /* Saisie directe dans le champ : la quantité tapée vaut pour le GROUPE.
     On répartit en écrivant l'écart sur la première ligne, ou en vidant par le bas
     si la cliente réduit. */
  function onChange(e) {
    var row = e.target.closest && e.target.closest('[data-pm-lines]');
    if (!row || !e.target.matches('input.qty-input')) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var qtys = nums(row, 'data-pm-qtys');
    var count = parseInt(row.getAttribute('data-pm-cartlines'), 10);
    var current = qtys.reduce(function (a, b) { return a + b; }, 0);
    var wanted = parseInt(String(e.target.value).replace(/\D/g, ''), 10);
    if (!isFinite(wanted) || wanted < 0) wanted = current;

    var next;
    if (wanted >= current) {
      next = qtys.slice();
      next[0] = next[0] + (wanted - current);
    } else {
      next = decrement(qtys, current - wanted);
    }

    row.classList.add('is-loading');
    applyLines(nums(row, 'data-pm-lines'), next, count);
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);
})();
